import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getLearnersById, updateLearner, updateStaffPassword, suspendLearner, deleteLearner, getFacilities, getJobTitles, getStates, getLgasByState } from "../api";
import "./HealthWorkerDetail.css";

export default function HealthWorkerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({});
  const [enrolments, setEnrolments] = useState([]);
  const [dateJoined, setDateJoined] = useState("");
  const [activityStatus, setActivityStatus] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState("");
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [loadingLocationData, setLoadingLocationData] = useState(false);
  const [loadingLgas, setLoadingLgas] = useState(false);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [jobTitles, setJobTitles] = useState([]);
  const [loadingJobTitles, setLoadingJobTitles] = useState(false);

  // 🔹 NEW states for password change
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  
  // States for suspend/activate and delete
  const [activating, setActivating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userStatus, setUserStatus] = useState('Active');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const learner = await getLearnersById(id);

        setFormData({
          firstname: learner.firstname || "",
          othernames: learner.othernames || "",
          lastname: learner.lastname || "",
          email: learner.email || "",
          phone: learner.phone_number || "",
          gender: learner.gender || "",
          dob: learner.dob || "",
          highest_qualification: learner.highest_qualification || "",
          job_title_id: learner.job_title?.id || learner.cadre?.id || learner.job_title_id || "",
          jobRole: learner.job_title?.title || learner.cadre?.name || "",
          facility_id: learner.facility_id || learner.facility?.id || "",
          facility: learner.facility?.name || "",
          lga: learner.lga?.name || "",
          state_name: learner.state?.name || learner.lga?.state?.name || "",
          state_id: learner.state_id || learner.state?.id || learner.lga?.state_id || learner.lga?.state?.id || "",
          lga_id: learner.lga_id || learner.lga?.id || ""
        });

        setEnrolments(learner.enrolments || []);
        setDateJoined(
          learner.created_at
            ? new Date(learner.created_at).toLocaleDateString()
            : "Unknown"
        );
        setActivityStatus(
          learner.enrolments?.length
            ? "Active recently"
            : "No recent activity"
        );
      } catch (err) {
        console.error(err);
        setError("Failed to load user data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchStates = async () => {
      setLoadingLocationData(true);
      try {
        const data = await getStates();

        const normalizedStates = (Array.isArray(data) ? data : [])
          .map((state) => ({
            id: state.id,
            name: state.name || state.state_name || "Unknown State",
          }))
          .filter((state) => state.id != null)
          .sort((a, b) => a.name.localeCompare(b.name));

        setStates(normalizedStates);
      } catch (err) {
        console.error("Failed to fetch states:", err);
      } finally {
        setLoadingLocationData(false);
      }
    };

    fetchStates();
  }, []);

  useEffect(() => {
    const fetchJobTitlesData = async () => {
      setLoadingJobTitles(true);
      try {
        const data = await getJobTitles();
        const normalized = data
          .map((jt) => ({
            id: jt.id || jt.job_title_id || jt.jobTitleId,
            name: jt.name || jt.title || jt.job_title_name || jt.label || "Unknown",
          }))
          .filter((jt) => jt.id != null && jt.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setJobTitles(normalized);
      } catch (err) {
        console.error("Failed to fetch job titles:", err);
      } finally {
        setLoadingJobTitles(false);
      }
    };
    fetchJobTitlesData();
  }, []);

  useEffect(() => {
    if (!formData.state_id) {
      setLgas([]);
      return;
    }

    let cancelled = false;

    const fetchLgas = async () => {
      setLoadingLgas(true);
      try {
        const data = await getLgasByState(formData.state_id);
        if (cancelled) return;

        const normalizedLgas = (Array.isArray(data) ? data : [])
          .map((lga) => ({
            id: lga.id || lga.lga_id || lga.lgaId,
            name: lga.name || lga.lga_name || lga.lgaName || "Unknown LGA",
          }))
          .filter((lga) => lga.id != null)
          .sort((a, b) => a.name.localeCompare(b.name));

        setLgas(normalizedLgas);

        if (
          formData.lga_id &&
          !normalizedLgas.some((lga) => String(lga.id) === String(formData.lga_id))
        ) {
          setFormData((prev) => ({ ...prev, lga_id: "" }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to fetch LGAs for selected state:", err);
          setLgas([]);
        }
      } finally {
        if (!cancelled) setLoadingLgas(false);
      }
    };

    fetchLgas();
    return () => { cancelled = true; };
    // formData.lga_id is only read to decide whether to clear a stale selection,
    // not to trigger a refetch — omitted from deps to avoid re-fetching on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.state_id]);

  useEffect(() => {
    if (!formData.lga_id) {
      setFacilityOptions([]);
      return;
    }
    let cancelled = false;
    const fetchFacsByLga = async () => {
      setLoadingFacilities(true);
      try {
        const facs = await getFacilities(formData.lga_id);
        if (!cancelled) {
          const sorted = [...facs].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setFacilityOptions(sorted);
        }
      } catch (err) {
        console.error("Failed to fetch facilities:", err);
        if (!cancelled) setFacilityOptions([]);
      } finally {
        if (!cancelled) setLoadingFacilities(false);
      }
    };
    fetchFacsByLga();
    return () => { cancelled = true; };
  }, [formData.lga_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "state_id") {
        return { ...prev, state_id: value, lga_id: "", facility_id: "" };
      }
      if (name === "lga_id") {
        return { ...prev, lga_id: value, facility_id: "" };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSave = async () => {
    setSaveError("");
    setSaveSuccess("");
    setSaving(true);
    try {
      await updateLearner(id, {
        firstname: formData.firstname,
        othernames: formData.othernames,
        lastname: formData.lastname,
        email: formData.email,
        phone_number: formData.phone,
        gender: formData.gender,
        dob: formData.dob || null,
        highest_qualification: formData.highest_qualification || null,
        job_title_id: formData.job_title_id ? Number(formData.job_title_id) : null,
        facility_id: formData.facility_id ? Number(formData.facility_id) : null,
        state_id: formData.state_id || null,
        lga_id: formData.lga_id || null
      });
      setIsEditing(false);
      setSaveSuccess("Staff updated successfully.");
      setTimeout(() => setSaveSuccess(""), 4000);
    } catch (err) {
      console.error(err.response?.data || err);
      setSaveError("Failed to update staff. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // 🔹 NEW: Handle password update
  const handlePasswordUpdate = async () => {
    if (!newPassword) {
      setPasswordMessage("Please enter a new password.");
      return;
    }

    try {
      setChangingPassword(true);
      setPasswordMessage("");
      await updateStaffPassword(id, newPassword);
      setPasswordMessage("✅ Password updated successfully!");
      setNewPassword("");
      setTimeout(() => setShowPasswordModal(false), 2000);
    } catch (err) {
      setPasswordMessage("❌ Failed to update password. Try again.");
      console.error(err);
    } finally {
      setChangingPassword(false);
    }
  };

  // Handle suspend/activate
  const handleSuspendActivate = async () => {
    const shouldSuspend = userStatus.toLowerCase() === 'active';
    
    if (!window.confirm(`Are you sure you want to ${shouldSuspend ? 'suspend' : 'unsuspend'} this health worker?`)) {
      return;
    }

    setActivating(true);
    try {
      await suspendLearner(id, shouldSuspend);
      // Refresh user data
      const learner = await getLearnersById(id);
      let status = 'Active';
      if (learner.is_active === 0 || learner.is_active === false) {
        status = 'Inactive/Suspended';
      } else if (learner.is_suspended === 1 || learner.is_suspended === true) {
        status = 'Inactive/Suspended';
      }
      setUserStatus(status);
      alert(`Health worker ${shouldSuspend ? 'suspended' : 'activated'} successfully!`);
    } catch (err) {
      console.error('Failed to update status:', err);
      alert(`Failed to ${shouldSuspend ? 'suspend' : 'activate'} health worker. Please try again.`);
    } finally {
      setActivating(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete this health worker? This action cannot be undone.`)) {
      setShowDeleteConfirm(false);
      return;
    }

    setDeleting(true);
    try {
      await deleteLearner(id);
      alert('Health worker deleted successfully!');
      navigate('/users');
    } catch (err) {
      console.error('Failed to delete health worker:', err);
      alert('Failed to delete health worker. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) return <div className="health-worker-detail-page">Loading...</div>;
  if (error) return <div className="health-worker-detail-page">{error}</div>;

  return (
    <div className="health-worker-detail-page">
      {/* Header */}
      <div className="health-worker-detail-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>

      <div className="health-worker-detail-content">
        {/* LEFT PANEL */}
        <div className="left-panel">
          {/* Avatar */}
          <div className="profile-picture-section">
            <div className="profile-picture">
              <div className="avatar-woman">
                <div className="woman-hair"></div>
                <div className="woman-face"></div>
                <div className="woman-top"></div>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="user-information-form">
            {[
              { label: "First Name", name: "firstname" },
              { label: "Middle Name", name: "othernames" },
              { label: "Last Name", name: "lastname" },
              { label: "Email", name: "email", type: "email", nullable: true },
              { label: "Phone", name: "phone" },
              { label: "Gender", name: "gender", type: "select" },
              { label: "Date of Birth", name: "dob", type: "date" },
              { label: "Highest Qualification", name: "highest_qualification", type: "qualification-select" },
              { label: "Job Role", name: "job_title_id", type: "jobtitle-select" },
              { label: "State", name: "state_id", type: "state-select" },
              { label: "LGA", name: "lga_id", type: "lga-select" },
              { label: "Facility", name: "facility_id", type: "facility-select" }
            ].map((field) => (
              <div className="form-group" key={field.name}>
                <label>{field.label}</label>
                {isEditing ? (
                  field.type === "select" ? (
                    <select
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  ) : field.type === "qualification-select" ? (
                    <select
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select qualification</option>
                      <option value="FSLC">FSLC (First School Leaving Certificate)</option>
                      <option value="JSCE">JSCE (Junior Secondary Certificate)</option>
                      <option value="SSCE">SSCE / WAEC / NECO</option>
                      <option value="OND">OND (Ordinary National Diploma)</option>
                      <option value="HND">HND (Higher National Diploma)</option>
                      <option value="NCE">NCE (Nigeria Certificate in Education)</option>
                      <option value="BSc">B.Sc / B.A / B.Ed (Bachelor's Degree)</option>
                      <option value="MBBS">MBBS / BDS</option>
                      <option value="PGD">PGD (Postgraduate Diploma)</option>
                      <option value="MSc">M.Sc / M.A / MBA (Master's Degree)</option>
                      <option value="PhD">Ph.D (Doctorate)</option>
                      <option value="Fellowship">Fellowship (e.g. FMCP, FWACS)</option>
                    </select>
                  ) : field.type === "jobtitle-select" ? (
                    <select
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      disabled={loadingJobTitles}
                    >
                      <option value="">
                        {loadingJobTitles ? "Loading..." : "Select job title"}
                      </option>
                      {jobTitles.map((jt) => (
                        <option key={jt.id} value={jt.id}>{jt.name}</option>
                      ))}
                    </select>
                  ) : field.type === "facility-select" ? (
                    <select
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      disabled={!formData.lga_id || loadingFacilities}
                    >
                      <option value="">
                        {!formData.lga_id ? "Select LGA first" : loadingFacilities ? "Loading facilities..." : "Select facility"}
                      </option>
                      {facilityOptions.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  ) : field.type === "state-select" ? (
                    <select
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      disabled={loadingLocationData}
                    >
                      <option value="">
                        {loadingLocationData ? "Loading states..." : "Select state"}
                      </option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "lga-select" ? (
                    <select
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      disabled={!formData.state_id || loadingLgas}
                    >
                      <option value="">
                        {!formData.state_id ? "Select state first" : loadingLgas ? "Loading LGAs..." : "Select LGA"}
                      </option>
                      {lgas.map((lga) => (
                        <option key={lga.id} value={lga.id}>
                          {lga.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      name={field.name}
                      type={field.type || "text"}
                      value={formData[field.name]}
                      onChange={handleChange}
                    />
                  )
                ) : (
                  <p>
                    {field.type === "state-select"
                      ? states.find((s) => String(s.id) === String(formData.state_id))?.name || formData.state_name || "N/A"
                      : field.type === "lga-select"
                      ? lgas.find((l) => String(l.id) === String(formData.lga_id))?.name || formData.lga || "N/A"
                      : field.type === "jobtitle-select"
                      ? jobTitles.find((jt) => String(jt.id) === String(formData.job_title_id))?.name || formData.jobRole || "N/A"
                      : field.type === "facility-select"
                      ? facilityOptions.find((f) => String(f.id) === String(formData.facility_id))?.name || formData.facility || "N/A"
                      : formData[field.name] || "N/A"}
                  </p>
                )}
              </div>
            ))}

            <div className="form-group">
              <label>Date Joined</label>
              <p>{dateJoined}</p>
            </div>

            {/* <div className="form-group">
              <label>Activity Status</label>
              <p>{activityStatus}</p>
            </div> */}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {saveSuccess && (
              <p style={{ color: "green", marginBottom: "8px", fontSize: "0.9rem" }}>{saveSuccess}</p>
            )}
            {isEditing && saveError && (
              <p style={{ color: "red", marginBottom: "8px", fontSize: "0.9rem" }}>{saveError}</p>
            )}
            {isEditing ? (
              <>
                <button className="btn-acc" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  className="btn-acc-del"
                  onClick={() => { setIsEditing(false); setSaveError(""); }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button className="btn-acc" onClick={() => setIsEditing(true)}>
                  Edit
                </button>

                {/* 🔹 NEW: Change Password button */}
                <button
                  className="btn-acc"
                  onClick={() => setShowPasswordModal(true)}
                >
                  Change Password
                </button>
                
                {/* Suspend/Activate button */}
                <button
                  className={`btn-acc ${userStatus.toLowerCase() === 'active' ? 'btn-suspend-action' : 'btn-activate-action'}`}
                  onClick={handleSuspendActivate}
                  disabled={activating}
                >
                  {activating ? (
                    'Processing...'
                  ) : userStatus.toLowerCase() === 'active' ? (
                    'Suspend User'
                  ) : (
                    'Unsuspend User'
                  )}
                </button>
                
                {/* Delete button */}
                <button
                  className="btn-acc-del"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete User'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="right-panel">
          <div className="info-card">
            <h3>Activity Status</h3>
            <div className="activity-status">
              <span
                className={`status-indicator ${
                  activityStatus.includes("Active") ? "active" : ""
                }`}
              ></span>
              <span>{activityStatus}</span>
            </div>
          </div>

          <div className="current-course-card">
            <h3>Current Enrolments</h3>
            {enrolments.length > 0 ? (
              enrolments.map((enrol) => (
                <div key={enrol.id} className="course-image">
                  <div className="course-thumbnail">
                    <div className="woman-on-phone"></div>
                  </div>
                  <p className="course-title">
                    {enrol.course?.title || "Untitled Course"}
                  </p>
                </div>
              ))
            ) : (
              <p>No current courses</p>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="password-modal-overlay">
          <div className="password-modal">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this health worker?</p>
            <p style={{ color: '#dc3545', fontSize: '14px', marginTop: '10px' }}>
              This action cannot be undone. All data associated with this user will be permanently removed.
            </p>
            <div className="modal-actions">
              <button
                className="btn-acc-del"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
              <button
                className="btn-acc"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔹 NEW Password Modal */}
      {showPasswordModal && (
        <div className="password-modal-overlay">
          <div className="password-modal">
            <h3>Change Password</h3>
            <input
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={changingPassword}
            />
            {passwordMessage && (
              <p className="password-message">{passwordMessage}</p>
            )}
            <div className="modal-actions">
              <button
                className="btn-acc"
                onClick={handlePasswordUpdate}
                disabled={changingPassword}
              >
                {changingPassword ? "Updating..." : "Update Password"}
              </button>
              <button
                className="btn-acc-del"
                onClick={() => setShowPasswordModal(false)}
                disabled={changingPassword}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
